import { mockCurrentUserData } from "../data/mockData/wlDemoData";
import { isBrowserSessionDemo, isDemoLocked } from "./demoConfig";

const STORAGE_KEY = "wl_demo_2fa";
const REAL_IDENTITY_KEY = "wl_real_user_identity";
const BACKUP_CODE_COUNT = 10;

function readJson(key, fallback) {
  if (typeof sessionStorage === "undefined") {
    return fallback;
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) {
      return fallback;
    }
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

function read2faOverrides() {
  return readJson(STORAGE_KEY, {});
}

function write2faOverrides(patch) {
  const next = { ...read2faOverrides(), ...(patch || {}) };
  writeJson(STORAGE_KEY, next);
  return next;
}

/** Browser-session user overlay (same key as browserSessionDemoApi). */
function readBrowserUserOverrides() {
  return readJson("wl_bs_user_overrides", {});
}

function writeBrowserUserOverrides(patch) {
  const next = { ...readBrowserUserOverrides(), ...(patch || {}) };
  writeJson("wl_bs_user_overrides", next);
  return next;
}

function readRealUserIdentityOverrides() {
  return readJson(REAL_IDENTITY_KEY, {});
}

/** Persist FastAPI identity fields; demo permissions/profile mock data stay unchanged. */
export function persistRealUserIdentity(realUser) {
  if (!realUser || typeof realUser !== "object") {
    return readRealUserIdentityOverrides();
  }

  const firstName = String(realUser.first_name || realUser.firstname || "").trim();
  const lastName = String(realUser.last_name || realUser.lastname || "").trim();
  const email = String(realUser.email || "").trim();
  const username = String(realUser.username || realUser.email || "").trim();

  if (!firstName && !lastName && !email && !username) {
    return readRealUserIdentityOverrides();
  }

  const patch = {
    ...(firstName ? { first_name: firstName } : {}),
    ...(lastName ? { last_name: lastName } : {}),
    ...(email ? { email } : {}),
    ...(username ? { username } : {}),
  };
  writeJson(REAL_IDENTITY_KEY, patch);
  return patch;
}

export function clearRealUserIdentityStorage() {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.removeItem(REAL_IDENTITY_KEY);
}

function applyDemoUserPermissions(merged) {
  if (!isDemoLocked()) {
    return merged;
  }
  return {
    ...merged,
    can_add_takedown: mockCurrentUserData.can_add_takedown !== false,
  };
}

export function getMergedDemoUser() {
  const base = { ...mockCurrentUserData };
  const realIdentity = readRealUserIdentityOverrides();
  let merged;
  if (isBrowserSessionDemo()) {
    merged = { ...base, ...readBrowserUserOverrides(), ...read2faOverrides(), ...realIdentity };
  } else {
    merged = { ...base, ...read2faOverrides(), ...realIdentity };
  }
  return applyDemoUserPermissions(merged);
}

export function patchDemoUserTwoFactor(patch) {
  if (!patch || typeof patch !== "object") {
    return getMergedDemoUser();
  }
  if (isBrowserSessionDemo()) {
    writeBrowserUserOverrides(patch);
  }
  write2faOverrides(patch);
  return getMergedDemoUser();
}

export function getDemoTwoFactorStatus() {
  const user = getMergedDemoUser();
  const method = user.two_factor_method || null;
  const available = ["email", "auth"];
  return {
    two_factor_method: method,
    available_methods: available,
    backup_codes_remaining: user.backup_codes_remaining ?? 8,
  };
}

function randomSegment(length, chars) {
  let out = "";
  for (let i = 0; i < length; i += 1) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

export function generateDemoBackupCodes() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const codes = Array.from({ length: BACKUP_CODE_COUNT }, () => {
    const left = randomSegment(4, chars);
    const right = randomSegment(4, chars);
    return `${left}-${right}`;
  });
  patchDemoUserTwoFactor({
    backup_codes_remaining: codes.length,
    last_backup_codes_generated_at: new Date().toISOString(),
  });
  return codes;
}

/** Deterministic pseudo-random cell from row/col/seed (QR-like grid). */
function cellFilled(row, col, seed) {
  const n =
    (seed * 9301 +
      row * 49297 +
      col * 233280 +
      ((row * col) % 17) * 1103515245) %
    2147483647;
  return (n % 100) < 48;
}

/**
 * Demo placeholder QR image (random-looking pattern, stable per seed).
 * @param {string|number} seed
 * @returns {string} data:image/png;base64,...
 */
export function generateDemoQrCodeDataUrl(seed = "demo") {
  if (typeof document === "undefined") {
    return "";
  }
  const numericSeed = String(seed)
    .split("")
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const size = 220;
  const cells = 25;
  const cellSize = Math.floor(size / cells);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return "";
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "#000000";
  for (let r = 0; r < cells; r += 1) {
    for (let c = 0; c < cells; c += 1) {
      const inFinder =
        (r < 7 && c < 7) ||
        (r < 7 && c >= cells - 7) ||
        (r >= cells - 7 && c < 7);
      const filled = inFinder ? (r + c) % 2 === 0 : cellFilled(r, c, numericSeed);
      if (filled) {
        ctx.fillRect(c * cellSize, r * cellSize, cellSize, cellSize);
      }
    }
  }
  return canvas.toDataURL("image/png");
}

export function clearDemoTwoFactorStorage() {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.removeItem(STORAGE_KEY);
  clearRealUserIdentityStorage();
}
